import type {
  EnvironmentId,
  ProjectId,
  PullRequestInvolvement,
  PullRequestListEntry,
  PullRequestListFilters,
  PullRequestListState,
  SourceControlProviderKind,
} from "@cmmd-forge/contracts";

import { parsePullRequestListSort, type PullRequestListSort } from "./pullRequestListSort.logic";

export interface PullRequestsSearch {
  readonly state: PullRequestListState;
  /** Rows within each inbox section, which the host's own order would otherwise fix. */
  readonly sort?: PullRequestListSort;
  readonly involvement?: PullRequestInvolvement;
  readonly environmentId?: EnvironmentId;
  readonly projectId?: ProjectId;
  readonly host?: string;
  readonly provider?: SourceControlProviderKind;
  readonly repository?: string;
  readonly number?: number;
  readonly query?: string;
  readonly draft?: PullRequestListFilters["draft"];
  readonly review?: PullRequestListFilters["review"];
  readonly checks?: PullRequestListFilters["checks"];
}

const MAX_QUERY_LENGTH = 200;
const MAX_QUALIFIER_VALUES = 10;
const QUERY_TOKEN = /(?:[^\s"]|"[^"]*")+/g;
const PROVIDERS = new Set<SourceControlProviderKind>([
  "github",
  "gitlab",
  "bitbucket",
  "azure-devops",
]);
const QUALIFIER_OPTIONS = {
  checks: { passing: "passing", failing: "failing" },
  draft: { true: "only", false: "hide" },
  review: {
    approved: "approved",
    "changes-requested": "changes-requested",
    "review-required": "review-required",
    none: "none",
  },
} as const;
const URL_FILTER_OPTIONS = {
  draft: { only: "only", hide: "hide" },
  review: QUALIFIER_OPTIONS.review,
  checks: QUALIFIER_OPTIONS.checks,
} as const;

function boundedString(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim().slice(0, MAX_QUERY_LENGTH);
  return trimmed.length > 0 ? trimmed : undefined;
}

function parseInvolvement(value: unknown): PullRequestInvolvement | undefined {
  return value === "reviewing" || value === "authored" || value === "all" ? value : undefined;
}

function parseState(value: unknown): PullRequestListState {
  return value === "closed" || value === "merged" || value === "all" ? value : "open";
}

function parseProvider(value: unknown): SourceControlProviderKind | undefined {
  return typeof value === "string" && PROVIDERS.has(value as SourceControlProviderKind)
    ? (value as SourceControlProviderKind)
    : undefined;
}

function parseNumber(value: unknown): number | undefined {
  const parsed = typeof value === "string" && /^\d+$/.test(value) ? Number(value) : value;
  return typeof parsed === "number" && Number.isSafeInteger(parsed) && parsed > 0
    ? parsed
    : undefined;
}

function parseFilterOption<Option extends string>(
  value: unknown,
  options: Readonly<Record<string, Option>>,
): Option | undefined {
  return typeof value === "string" ? options[value] : undefined;
}

function withoutUndefinedValues(values: Record<string, unknown>): PullRequestsSearch {
  return Object.fromEntries(
    Object.entries(values).filter(([, value]) => value !== undefined),
  ) as unknown as PullRequestsSearch;
}

/**
 * Whether anything in the search narrows the inbox. A sort is an ordering, not
 * a narrowing, so it never lights the filters affordance.
 */
export function hasActivePullRequestFilters(search: PullRequestsSearch): boolean {
  return (
    search.state !== "open" ||
    Object.entries(search).some(
      ([key, value]) => key !== "state" && key !== "sort" && value !== undefined,
    )
  );
}

export function parsePullRequestsSearch(
  input: string | Readonly<Record<string, unknown>>,
): PullRequestsSearch {
  const raw: Readonly<Record<string, unknown>> =
    typeof input === "string" ? Object.fromEntries(new URLSearchParams(input)) : input;
  const query = parsePullRequestQuery(boundedString(raw["q"]) ?? "") ?? undefined;
  return withoutUndefinedValues({
    state: parseState(raw["state"]),
    sort: parsePullRequestListSort(raw["sort"]),
    involvement: parseInvolvement(raw["involvement"]),
    environmentId: boundedString(raw["environmentId"]) as EnvironmentId | undefined,
    projectId: boundedString(raw["projectId"]) as ProjectId | undefined,
    host: boundedString(raw["host"]),
    provider: parseProvider(raw["provider"]),
    repository: boundedString(raw["repository"]),
    number: parseNumber(raw["number"]),
    query: query === "" ? undefined : query,
    draft: parseFilterOption(raw["draft"], URL_FILTER_OPTIONS.draft),
    review: parseFilterOption(raw["review"], URL_FILTER_OPTIONS.review),
    checks: parseFilterOption(raw["checks"], URL_FILTER_OPTIONS.checks),
  });
}

function qualifierValue(raw: string): string {
  return raw.replaceAll('"', "").trim().slice(0, MAX_QUERY_LENGTH);
}

function qualifierNames(raw: string): ReadonlyArray<string> {
  const quoted = /^\s*"[^"]*"\s*$/.test(raw);
  const values = quoted ? [qualifierValue(raw)] : raw.split(",").map(qualifierValue);
  return values.filter(Boolean).slice(0, MAX_QUALIFIER_VALUES);
}

export interface ParsedPullRequestQuery {
  readonly text: string;
  readonly filters: PullRequestListFilters;
}

interface PullRequestQualifier {
  readonly key: string;
  readonly negated: boolean;
  readonly rawValue: string;
  readonly value: string;
}

interface PullRequestQualifierAccumulator {
  readonly labels: string[][];
  readonly excludedLabels: string[];
  author?: string;
  draft?: PullRequestListFilters["draft"];
  review?: PullRequestListFilters["review"];
  checks?: PullRequestListFilters["checks"];
}

function parseQualifier(token: string): PullRequestQualifier | undefined {
  const match = /^(-?)([A-Za-z][A-Za-z0-9_-]*):(.*)$/.exec(token);
  if (match === null) return undefined;
  return {
    key: match[2]!.toLowerCase(),
    negated: match[1] === "-",
    rawValue: match[3]!,
    value: qualifierValue(match[3]!),
  };
}

function applyLabels(
  qualifier: PullRequestQualifier,
  accumulator: PullRequestQualifierAccumulator,
) {
  const names = qualifierNames(qualifier.rawValue);
  if (names.length === 0) return false;
  if (qualifier.negated) accumulator.excludedLabels.push(...names);
  else accumulator.labels.push([...names]);
  return true;
}

function applyQualifierOption<Option extends string>(
  value: string,
  options: Readonly<Record<string, Option>>,
  apply: (option: Option) => void,
): boolean {
  const option = options[value];
  if (option === undefined) return false;
  apply(option);
  return true;
}

function applyNonNegatedQualifier(
  qualifier: PullRequestQualifier,
  accumulator: PullRequestQualifierAccumulator,
): boolean {
  if (qualifier.key === "author" && qualifier.value.length > 0) {
    accumulator.author = qualifier.value;
    return true;
  }
  if (qualifier.key === "draft") {
    return applyQualifierOption(qualifier.value, QUALIFIER_OPTIONS.draft, (draft) => {
      accumulator.draft = draft;
    });
  }
  if (qualifier.key === "review") {
    return applyQualifierOption(qualifier.value, QUALIFIER_OPTIONS.review, (review) => {
      accumulator.review = review;
    });
  }
  if (qualifier.key === "checks" || qualifier.key === "status") {
    return applyQualifierOption(qualifier.value, QUALIFIER_OPTIONS.checks, (checks) => {
      accumulator.checks = checks;
    });
  }
  return false;
}

function applyQualifier(
  qualifier: PullRequestQualifier,
  accumulator: PullRequestQualifierAccumulator,
): boolean {
  if (qualifier.key === "label") return applyLabels(qualifier, accumulator);
  return !qualifier.negated && applyNonNegatedQualifier(qualifier, accumulator);
}

export function parsePullRequestQualifierQuery(raw: string): ParsedPullRequestQuery {
  const text: string[] = [];
  const accumulator: PullRequestQualifierAccumulator = {
    labels: [],
    excludedLabels: [],
  };
  for (const [token] of raw.slice(0, MAX_QUERY_LENGTH).matchAll(QUERY_TOKEN)) {
    const qualifier = parseQualifier(token);
    if (qualifier === undefined || !applyQualifier(qualifier, accumulator))
      text.push(token.replaceAll('"', ""));
  }
  return {
    text: text.join(" ").trim(),
    filters: {
      ...(accumulator.labels.length === 0 ? {} : { labels: accumulator.labels }),
      ...(accumulator.excludedLabels.length === 0
        ? {}
        : { excludedLabels: accumulator.excludedLabels }),
      ...(accumulator.author === undefined ? {} : { author: accumulator.author }),
      ...(accumulator.draft === undefined ? {} : { draft: accumulator.draft }),
      ...(accumulator.review === undefined ? {} : { review: accumulator.review }),
      ...(accumulator.checks === undefined ? {} : { checks: accumulator.checks }),
    },
  };
}

export function parsePullRequestQuery(raw: string): string | null {
  const trimmed = raw.trim();
  // Matching control characters is the point: this rejects a query carrying C0
  // or DEL instead of forwarding it.
  // eslint-disable-next-line no-control-regex
  if (trimmed.length > MAX_QUERY_LENGTH || /[\u0000-\u001f\u007f]/.test(trimmed)) return null;
  return trimmed;
}

export interface PullRequestEnvironmentInboxResult<Entry extends object> {
  readonly environmentId: EnvironmentId;
  readonly entries: ReadonlyArray<Entry>;
  readonly isPending: boolean;
  readonly error: Error | null;
}

export function aggregatePullRequests<Entry extends { readonly updatedAt: string }>(
  results: ReadonlyArray<PullRequestEnvironmentInboxResult<Entry>>,
) {
  const entries = results
    .flatMap((result) =>
      result.entries.map((entry, index) => ({
        ...entry,
        environmentId: result.environmentId,
        rowKey: `${result.environmentId}:${index}:${"host" in entry ? String(entry.host) : ""}:${"repository" in entry ? String(entry.repository) : ""}#${"number" in entry ? String(entry.number) : ""}`,
      })),
    )
    .toSorted((left, right) => right.updatedAt.localeCompare(left.updatedAt));
  return {
    entries,
    isPending: results.some((result) => result.isPending),
    errors: results.flatMap((result) =>
      result.error === null
        ? []
        : [{ environmentId: result.environmentId, message: result.error.message }],
    ),
  };
}

export function findDeepLinkedPullRequest<
  Entry extends Pick<PullRequestListEntry, "provider" | "host" | "repository" | "number"> & {
    readonly environmentId: EnvironmentId;
  },
>(entries: ReadonlyArray<Entry>, search: PullRequestsSearch): Entry | undefined {
  if (search.repository === undefined || search.number === undefined) return undefined;
  return entries.find(
    (entry) =>
      (search.environmentId === undefined || entry.environmentId === search.environmentId) &&
      (search.provider === undefined || entry.provider === search.provider) &&
      (search.host === undefined || entry.host === search.host) &&
      entry.repository === search.repository &&
      entry.number === search.number,
  );
}
